import { useCallback, useEffect, useState } from "react";

/**
 * useDensity — per-user UI density preference.
 *
 * Two modes:
 *   "comfortable" (default) — current spacing
 *   "compact"               — shrinks every reusable primitive via CSS
 *                              variables in index.css (`html[data-density="compact"]`)
 *
 * Stored in localStorage so the choice survives reloads and applies
 * before React boots (we set it from the inline script in public/index.html
 * to avoid a flash of comfortable-mode on hard refresh).
 */
const STORAGE_KEY = "bd_density";
const VALID = ["comfortable", "compact"];

function applyDensity(d) {
  if (typeof document === "undefined") return;
  const safe = VALID.includes(d) ? d : "comfortable";
  document.documentElement.setAttribute("data-density", safe);
}

function readInitial() {
  if (typeof window === "undefined") return "comfortable";
  try {
    const v = window.localStorage?.getItem(STORAGE_KEY);
    if (VALID.includes(v)) return v;
  } catch (e) { /* ignore */ }
  return "comfortable";
}

export function useDensity() {
  const [density, setDensityState] = useState(readInitial);

  useEffect(() => {
    applyDensity(density);
    try { window.localStorage?.setItem(STORAGE_KEY, density); } catch (e) { /* ignore */ }
  }, [density]);

  const setDensity = useCallback((next) => {
    setDensityState(VALID.includes(next) ? next : "comfortable");
  }, []);

  return { density, setDensity };
}

// Helper for one-time bootstrap from index.js so the attribute is on
// `<html>` before React paints (avoids any flicker of comfortable spacing
// on a returning user who's chosen compact).
export function bootstrapDensity() {
  applyDensity(readInitial());
}
